import { useState, useRef, useCallback } from 'react';
import { fetchStockData } from '../data/dataService';
import { getDefaultChartOptions, getYAxis, formatCompactCurrency } from '../config/chartConfig';
import { getDefaultRatioChartOptions, getRatioYAxisTitle, getRatioValueTooltip } from '../config/ratioChartConfig';
import { computeDefaultRange } from '../config/rangeSelectorConfig';
import {
  METRIC_OPTIONS, METRIC_LABELS, METRIC_COLORS, ratioName, yieldName, ratioCeiling, METRIC_NATIVE_BASIS,
} from '../config/metricsConfig';
import {
  buildActualSeries, buildFairValueSeries, buildDividendFairValueSeries, buildRatioSeries, buildEstimatedPlotBand, stepJoin,
  convertBasis, interpJoin, applySbcAdjustment,
} from './seriesMath';
import { computeRatioOverlay, computeFairValueOverlay, computeRatioAxisMax, computeWindowStats, buildFairValueSkeleton, overlayFromSkeleton } from './overlayStats';
import { logSafe } from './logSafeSeries';

const RATIO_METRICS = METRIC_OPTIONS.map(({ value }) => value);

/**
 * "Adjust for SBC" toggle (2026-10-30 — metrics.sbc, SCORE_CARDS.md §1.6).
 * Applies `applySbcAdjustment` to ONLY `fcf`/`ocf` out of
 * `rawMetricSeriesByMetric` — EPS, EBITDA, net income, revenue and
 * dividends are untouched, per the approved design. Returns
 * `rawMetricSeriesByMetric` itself (not a copy) when the toggle is off, so
 * callers can rely on reference equality to skip unnecessary rebuilds.
 */
const withSbcAdjustment = (rawMetricSeriesByMetric, sbcSeries, adjustForSbc) => {
  if (!adjustForSbc) return rawMetricSeriesByMetric;
  const revenueSeries = rawMetricSeriesByMetric.revenue;
  return {
    ...rawMetricSeriesByMetric,
    fcf: applySbcAdjustment(rawMetricSeriesByMetric.fcf, sbcSeries, revenueSeries),
    ocf: applySbcAdjustment(rawMetricSeriesByMetric.ocf, sbcSeries, revenueSeries),
  };
};

/**
 * The upper chart's fair-value overlay is always computed from a MULTIPLE
 * (ratio-mode) series, independent of the lower ratio chart's Ratio/Yield
 * toggle (see buildMainChartSeries' docstring). This builds that dedicated
 * per-metric ratio series once, in 'ratio' mode with excludeLossPeriods off
 * (a yield-only flag with no bearing on the multiple basis) — so it depends
 * only on price/shares/netDebt/valueBasis/metricSeries and is untouched by
 * the toggle or its exclusion checkbox. Kept alongside `ratioSeriesByMetric`
 * in lastFetchRef and recomputed wherever those inputs change (fetch,
 * valueBasis), but NOT on ratioMode/excludeLossPeriods changes.
 */
const buildFairValueRatioSeriesByMetric = (price, shares, netDebt, valueBasis, metricSeriesByMetric) => {
  const out = {};
  RATIO_METRICS.forEach((m) => {
    out[m] = buildRatioSeries(price, shares, netDebt, valueBasis, m, metricSeriesByMetric[m], 'ratio', false);
  });
  return out;
};

const SERIES_NAMES = {
  marketCap: { perShare: 'Price', total: 'Market Cap' },
  enterpriseValue: { perShare: 'EV / Share', total: 'Enterprise Value' },
};

// NOTE: the main-chart legend (the series labels below the plot) shows PLAIN
// names — "Price" / "Earnings" / "Dividends" — with NO P/E or yield in
// brackets. Those valuation figures live ONLY in the hover tooltip (see the
// *TooltipRow builders below). The Key Facts panel (Analysis.js) is the place
// for the current blended P/E and dividend yield.

/**
 * Row renderer for the fair-value ("× Multiple") line in the main chart's
 * shared tooltip (see chartConfig.js's getMainChartTooltip). With `includeRaw`
 * (the "× Multiple" line) it emits ONE row: the raw reported metric at the
 * current display scale, with its per-date YIELD in brackets after the value
 * (e.g. "Earnings: 5.00 (4.12%)"). With `includeRaw: false` (the avg line) it
 * emits NOTHING — the tooltip is deliberately a clean 3-row box
 * (Price / Earnings / Dividend), so neither the manual nor the average
 * implied-fair-value row ("Price at P/E × N") is shown: the multiple is
 * already conveyed, per-date, by the P/E bracket on the Price row, and those
 * extra rows repeated "P/E" and pushed Dividend out of order. Returns a
 * `(point) => html` stashed on the series' `custom.tooltipRow`; the shared
 * tooltip finds each series' point nearest the hovered date and calls this, so
 * a series on sparse report timestamps still shows its nearest *actual*
 * reported value rather than dropping out.
 */
/** Percent string for a bracketed yield figure, e.g. "4.12%". */
const formatPct = (v) => `${Number(v).toFixed(2)}%`;

/**
 * Zips a fair-value overlay's avg-line points (`avgData`, {x,y,...} objects)
 * with its ±1σ band (`bandData`, [x, low, high] triples aligned index-for-
 * index) into avg points that also carry their own `low`/`high`. Both the
 * Price row's valuation arrow and the historical-average row's range read
 * these off the point, so they must ride along with the data — including
 * through the drag-zoom path (recomputeOverlaysForWindow), which imperatively
 * setData()s the avg line and would otherwise strip a low/high attached only
 * in the full-rebuild path. Degrades to the bare avg point when there's no
 * band at that index (empty overlay), so downstream rows drop their range
 * rather than showing a half-open "95–".
 */
const withBandBounds = (avgData, bandData) => avgData.map((p, i) => {
  const band = bandData[i];
  return band ? { ...p, low: band[1], high: band[2] } : { ...p };
});

/**
 * Nearest-by-x value in a `[ts, value]` ratio series to timestamp `t`, or
 * null past its coverage. The avg line is price-aligned but the ratio series
 * sits on price days too, so most hits are exact; the nearest fallback covers
 * the forward-estimate tail's own (non-price) timestamps, where the ratio
 * barely moves day-to-day so the nearest price-day ratio is the right figure.
 * Null past the series' last point (no ratio in the pure forecast tail).
 */
const ratioAtFrom = (ratioSeries, t) => {
  if (!ratioSeries.length) return null;
  if (t > ratioSeries[ratioSeries.length - 1][0]) return null;
  let best = null;
  let bestGap = Infinity;
  for (let i = 0; i < ratioSeries.length; i++) {
    const gap = Math.abs(ratioSeries[i][0] - t);
    if (gap < bestGap) { bestGap = gap; best = ratioSeries[i][1]; }
    else if (ratioSeries[i][0] > t) break;
  }
  return best;
};

/**
 * Enrich avg-line points (already carrying ±1σ low/high via withBandBounds)
 * with the two extra fields the shared tooltip's avg-line renderer reads:
 *  - `ratio`: the per-date valuation multiple (P/E) at that point, so the
 *    "Earnings" row can show its reciprocal (earnings yield) in brackets.
 *  - `estDetail`: the analyst high/low band + contributor count at that date,
 *    present ONLY on forward-estimate points (looked up by exact timestamp
 *    against `estDetailByTs`, whose keys are the metric's own estimate
 *    timestamps — the same ones the avg line's forecast tail sits on).
 * Used by BOTH the full-rebuild path (buildMainChartSeries) and the drag-zoom
 * imperative path (recomputeOverlaysForWindow), so the earnings-yield and
 * estimate-detail rows stay live across zoom rather than going blank on the
 * imperative setData that would otherwise strip them.
 */
const enrichAvgPoints = (boundedPoints, ratioSeries, estDetailByTs) => boundedPoints.map((p) => ({
  ...p,
  ratio: ratioAtFrom(ratioSeries, p.x),
  estDetail: estDetailByTs.get(p.x),
}));

/**
 * Keys raw [t, {high, low, count}] estimate-detail entries by timestamp,
 * converting the high/low band from the metric's native basis to the
 * current display scale (shares at that timestamp via interpJoin, same
 * per-point lookup buildFairValueSeries uses for rawValue) so a total-basis
 * metric's analyst range doesn't render under a per-share value or vice
 * versa. No-op per entry when the metric's native basis already matches
 * displayScale, or when the entry carries no band (count-only "n=…"
 * entries pass through unconverted). Shared by both the full-rebuild path
 * (buildMainChartSeries) and the drag-zoom/skeleton path (enrichSkeleton)
 * so neither renders the raw native-basis figures.
 */
const estDetailMap = (entries, metric, displayScale, shares) => {
  const nativeBasis = METRIC_NATIVE_BASIS[metric];
  return new Map((entries || []).map(([t, d]) => {
    if (nativeBasis === displayScale || d.high == null || d.low == null) return [t, d];
    const sh = interpJoin([[t, null]], shares)[0][1];
    return [t, {
      ...d,
      high: convertBasis(d.high, nativeBasis, displayScale, sh),
      low: convertBasis(d.low, nativeBasis, displayScale, sh),
    }];
  }));
};

const enrichSkeleton = (skeleton, ratioSeries, estimatesDetailForMetric, metric, displayScale, shares) => {
  const filtered = (ratioSeries || []).filter(([, r]) => r != null);
  const estMap = estDetailMap(estimatesDetailForMetric, metric, displayScale, shares);
  return skeleton.map((p) => ({ ...p, ratio: ratioAtFrom(filtered, p.x), estDetail: estMap.get(p.x) }));
};

/**
 * Nearest-by-x point in a Highcharts series' live processed points to a
 * timestamp `x`. Used by the Price row to read the historical-average band
 * edges off the AVG series' own points at hover time (rather than baking
 * bounds onto Price at build time) — the avg/band recompute per window on
 * drag-zoom via recomputeOverlaysForWindow, so reading them live keeps the
 * valuation arrow correct across zoom without also re-setData'ing the Price
 * series. Null OUTSIDE the band's coverage on either side (before its first
 * point or past the forecast tail) or when the series is absent, so the arrow
 * degrades to a bare Price value there rather than comparing price against
 * back/forward-extrapolated edges.
 */
const nearestSeriesPoint = (series, x) => {
  const pts = series?.points;
  if (!pts || !pts.length) return null;
  if (x < pts[0].x || x > pts[pts.length - 1].x) return null;
  let best = null;
  let bestGap = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const gap = Math.abs(pts[i].x - x);
    if (gap < bestGap) { bestGap = gap; best = pts[i]; }
    else if (pts[i].x > x) break;
  }
  return best;
};

/**
 * 3-way valuation color for the Price row's P/E number, comparing price
 * against the historical-average band EDGES (±1σ), not the avg line itself:
 * inside the ±1σ margin of error price is "fairly valued" (neutral), only
 * outside is meaningfully rich/cheap. Red above the range (overvalued), green
 * below (undervalued), neutral within. Null band (no coverage at that date, or
 * missing price) -> null, so the caller renders the P/E in the default text
 * color rather than implying a valuation signal it can't support. */
const VALUATION_COLORS = { rich: '#d64545', fair: '#8a8a8a', cheap: '#3a9d5d' };

// The main chart's avg valuation line (and its ±1σ band) is ALWAYS this blue,
// regardless of which metric is selected — it's a single, fixed valuation
// reference, not a per-metric series. (It previously took METRIC_COLORS[metric]
// and so only happened to be blue for Earnings, whose palette slot is blue.)
// The lower ratio chart still colors each metric's avg line by METRIC_COLORS so
// multiple ticked metrics stay distinguishable there.
const MAIN_AVG_VALUATION_COLOR = '#2a78d6';
const valuationColor = (price, low, high) => {
  if (low == null || high == null || price == null) return null;
  if (price > high) return VALUATION_COLORS.rich;
  if (price < low) return VALUATION_COLORS.cheap;
  return VALUATION_COLORS.fair;
};

const fairValueTooltipRow = (metric, displayScale, valueBasis, multiple, includeRaw = true, selectedMultiple = null) => {
  const formatValue = (v) => (displayScale === 'total' ? formatCompactCurrency(v) : Number(v).toFixed(2));
  const metricLabel = METRIC_LABELS[metric] || 'Earnings';
  return (point) => {
    // The "× Multiple" fair-value line (includeRaw:true) no longer contributes
    // a row: the Earnings row was removed and its P/E moved onto the Price row
    // (color-coded). Only the AVG valuation line (includeRaw:false) renders.
    if (includeRaw) return '';
    // The AVG valuation line's row: the price IMPLIED by the historical-
    // average multiple at this date (metric × avg multiple) with its ±1σ RANGE
    // in brackets. "Price @ hist. avg" reads it correctly as an implied price
    // — where price WOULD sit at its historical-average valuation — rather
    // than "the historical average valuation" as an abstract figure. The label
    // is long, so it breaks onto its own line: value + range render on the
    // next line, indented (the only multi-line row in the box). The parens
    // carry the range, so the label needs no "range" word. Range degrades to a
    // bare value where the point has no band (see withBandBounds / forecast
    // tail).
    //
    // The label ALSO names the historical-average valuation itself — the avg
    // multiple this implied price is built from (e.g. "Price @ hist. avg P/E
    // 17.7"). `multiple` here is that window-derived avg multiple (the
    // `avgMultiple`/`fairValueOverlay.multiple` both call sites pass in), and
    // `ratioName(metric, valueBasis)` tracks the metric + Mkt Cap/EV toggle
    // (P/E vs EV/E, …). It's re-threaded on every drag-zoom via
    // recomputeOverlaysForWindow (which rebuilds this closure with the new
    // window's multiple), so the named avg valuation stays live and in sync
    // with the line it labels. Omitted when there's no resolved multiple
    // (empty/collapsed overlay) so the row degrades to the bare implied price.
    const avgValuationLabel = multiple != null && multiple > 0
      ? ` ${ratioName(metric, valueBasis)} ${Number(multiple).toFixed(1)}`
      : '';
    // ±1σ band rendered as a single "± half-width" figure rather than the
    // full low–high range, to keep the row compact. The main chart's band is
    // built on the multiple basis (computeFairValueOverlay is multiple-only —
    // no yield mode), so it's exactly metric × (avg ± std): symmetric about y,
    // and (high − low) / 2 == metric × std exactly. Degrades to no suffix
    // where the point has no band.
    const rangeSuffix = point.low != null && point.high != null
      ? ` (±${formatValue((point.high - point.low) / 2)})`
      : '';
    const avgRow = `<span style="color:${point.color}">●</span> Price @ hist. avg${avgValuationLabel}:<br/>&nbsp;&nbsp;&nbsp;<b>${formatValue(point.y)}</b>${rangeSuffix}<br/>`;

    // Price implied by the currently-SELECTED multiple (the manual "×
    // Multiple" line's value), directly below the hist.-avg row so the two
    // implied prices read together: "what price at the average valuation" vs
    // "what price at the multiple I picked". point.rawValue is the metric
    // value at this date (in the current display scale); × selectedMultiple is
    // the implied price. The row is dot-less/indented to hang under the avg
    // row's bullet rather than start a new bulleted line. Omitted when there's
    // no selected multiple or no metric value at this point.
    const selectedRow = (selectedMultiple != null && selectedMultiple > 0 && point.rawValue != null)
      ? `<span style="color:orange">●</span> Price @ ${ratioName(metric, valueBasis)} ${Number(selectedMultiple).toFixed(1)}: <b>${formatValue(point.rawValue * selectedMultiple)}</b><br/>`
      : '';

    // Earnings (the metric's own value) with its EARNINGS YIELD in brackets —
    // mirrors the Dividends row. Yield is the reciprocal of the P/E at this
    // date (point.ratio, attached in enrichAvgPoints), scale-invariant so it
    // reads the same per-share/total. Omitted where there's no ratio (forecast
    // tail past the ratio series' coverage) or no metric value.
    const earningsYield = point.ratio != null && point.ratio > 0 ? (100 / point.ratio) : null;
    const earningsSuffix = earningsYield != null ? ` (${formatPct(earningsYield)})` : '';
    const earningsRow = point.rawValue != null
      ? `<span style="color:${point.color}">●</span> ${metricLabel}: <b>${formatValue(point.rawValue)}</b>${earningsSuffix}<br/>`
      : '';

    // Estimate detail on forward-estimate points only: the high/low band with
    // the analyst count appended in brackets (e.g. "Range: 5.50–7.50 (n=30)"),
    // attached as point.estDetail in enrichAvgPoints (undefined on historical
    // points, so this whole block is skipped there). The band renders only
    // when present (null -> omitted, never a fabricated 0); the count hangs off
    // the range in brackets, or stands alone as "Estimates: n=…" when there's
    // no band to attach to. Implied estimates carry neither.
    let estimateRows = '';
    if (point.estDetail) {
      const { high, low, count } = point.estDetail;
      const countSuffix = count != null ? ` (n=${count})` : '';
      if (high != null && low != null) {
        estimateRows += `&nbsp;&nbsp;&nbsp;Range: <b>${formatCompactCurrency(low)}–${formatCompactCurrency(high)}</b>${countSuffix}<br/>`;
      } else if (count != null) {
        estimateRows += `&nbsp;&nbsp;&nbsp;Estimates: <b>n=${count}</b><br/>`;
      }
    }

    return avgRow + selectedRow + earningsRow + estimateRows;
  };
};

/**
 * Row renderer for the dividend fair-value line's FY tooltip rows. Emits the
 * same two-row implied/fair-value pair as fairValueTooltipRow (dividend ×
 * multiple, on the current display scale), plus a dedicated line showing the
 * dividend AMOUNT and dividend YIELD at that fiscal-year report point. The
 * label is always "Dividends" (per-share vs total is clear from the chart's
 * display-scale toggle); yield is scale-invariant (per-share ÷ per-share) so
 * it reads the same in both modes. Yield is omitted when the point carries
 * none (no price coverage at that date). The plotted dividends are REGULAR
 * dividends only (the backend splits specials out, ADR 0013 in
 * funviz-amplify); a point whose window also held a special dividend gets a
 * "Special dividends: X" line, and no line otherwise.
 */
const EIGHTEEN_MONTHS_MS = 18 * 30.44 * 24 * 3600 * 1000;

const dividendTooltipRow = (displayScale) => {
  const formatValue = (v) => (displayScale === 'total' ? formatCompactCurrency(v) : Number(v).toFixed(2));
  // Always "Dividends" — per-share vs total is already clear from the chart's
  // display-scale toggle, so the label no longer swaps to "Dividends/Share".
  // `rawValue` is in the current display scale.
  const amountLabel = 'Dividends';
  return (point, hoveredX) => {
    if (hoveredX != null && point.x != null && Math.abs(hoveredX - point.x) > EIGHTEEN_MONTHS_MS) return '';
    // Dividend YIELD at this report date in brackets (e.g. "Dividends
    // (1.09%)") — dividend-per-share ÷ price-per-share at that date, attached
    // as `divYield` to the point in buildMainChartSeries. Scale-invariant, so
    // it reads the same in per-share or total; omitted when there's no price
    // coverage at the date to divide by.
    const suffix = point.divYield != null && point.divYield > 0 ? ` (${formatPct(point.divYield)})` : '';
    const row = `<span style="color:${point.color}">●</span> ${amountLabel}: <b>${formatValue(point.rawValue)}</b>${suffix}<br/>`;
    // The plotted dividends are regular-only; a nonzero special amount over
    // the same trailing window gets its own indented line. Per-share
    // special × (rawValue / divPerShare) puts it on the same display scale
    // as the Dividends figure (that ratio is the share count in Total mode,
    // 1 in Per Share mode).
    if (!(point.specialDividend > 0) || !(point.divPerShare > 0)) return row;
    const special = point.specialDividend * (point.rawValue / point.divPerShare);
    return `${row}&nbsp;&nbsp;&nbsp;Special dividends: <b>${formatValue(special)}</b><br/>`;
  };
};

/**
 * Row renderer for the actual value series (Price/Market Cap/EV) in the
 * shared tooltip — a single "{name}: {value}" line, matching the old
 * getValueTooltip output for both display scales.
 */
// One day in ms — tolerance for "is this hover past the Price series' last
// real point". Price is a daily series, so a nearest point more than a day
// before the hovered date means we're in the estimate/forecast region (the
// Price line has no data there and nearestPoint clamped to its last actual
// point), where the Price row should drop out entirely.
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const valueTooltipRow = (displayScale, valueBasis, metric) => (point, hoveredX) => {
  // Drop the Price row for dates beyond price's real coverage (the estimate
  // area). nearestPoint clamps to the last actual price point, so without this
  // the row would repeat the last known price at every future date. Only the
  // fair-value/avg lines (which have genuine forecast points) should speak
  // there.
  if (hoveredX != null && point.x != null && hoveredX - point.x > ONE_DAY_MS) return '';
  const formatValue = (v) => (displayScale === 'total' ? formatCompactCurrency(v) : Number(v).toFixed(2));
  // Price row carries the per-date valuation MULTIPLE (P/E, or EV/E under the
  // enterprise-value toggle via ratioName) in brackets after the value, with
  // the P/E NUMBER itself 3-way color-coded by where price sits vs the
  // historical-average band edges (±1σ): red overvalued / neutral fair /
  // green undervalued. The band edges are read LIVE off the avg series'
  // points (fairvalue-avg-metric, which carries low/high via withBandBounds),
  // nearest the hovered date — so the color tracks the per-window band
  // recompute on drag-zoom without the Price series needing its own
  // imperative update. Bracket omitted where the point has no ratio at that
  // date (forecast tail past price coverage); color falls back to default
  // text color where there's no band to judge against.
  let suffix = '';
  if (point.ratio != null && point.ratio > 0) {
    const avgSeries = point.series?.chart?.get('fairvalue-avg-metric');
    const bandPoint = nearestSeriesPoint(avgSeries, point.x);
    const color = valuationColor(point.y, bandPoint?.low, bandPoint?.high);
    const peText = `${ratioName(metric, valueBasis)} ${point.ratio.toFixed(1)}`;
    const pe = color ? `<span style="color:${color}">${peText}</span>` : peText;
    suffix = ` (${pe})`;
  }
  return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatValue(point.y)}</b>${suffix}<br/>`;
};

/**
 * Builds the main chart's series array: the 3 fixed series (actual
 * Price/Market Cap/EV line, the manual "Metric × Multiple" line, and the
 * empty click-to-measure performance-line placeholder) exactly as before,
 * plus exactly one avg-fair-value line + ±1σ band for the single currently
 * selected `metric` — the main chart only ever compares price/value to one
 * underlying metric (unlike the ratio chart, which can show several ticked
 * metrics at once).
 *
 * The overlay is always computed from a MULTIPLE (ratio-mode) series for
 * this metric — `fairValueRatioSeriesByMetric[metric]` — restricted to
 * [xMin, xMax] but drawn across that metric's full series. This is
 * deliberately independent of the lower ratio chart's Ratio/Yield toggle:
 * that toggle is a presentation choice for the lower chart only, and must
 * not change the upper chart's valuation methodology (averaging a P/E is a
 * different, non-equivalent operation from reciprocating an averaged
 * earnings yield — see the fair-value overlay's own module). The upper
 * chart therefore has one canonical basis (the multiple) and one label form
 * (`ratioName`, e.g. "Avg P/E"), regardless of the lower toggle.
 */
const buildMainChartSeries = ({
  price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple,
  metricSeriesByMetric, fairValueRatioSeriesByMetric, estimatesDetail = {}, specialDividends = [], xMin, xMax,
}) => {
  const { avgData, bandData, multiple: avgMultiple } = computeFairValueOverlay({
    ratioSeries: fairValueRatioSeriesByMetric[metric],
    metricSeries: metricSeriesByMetric[metric],
    // The upper chart's overlay is multiple-only by construction — it reads
    // the dedicated always-ratio series (fairValueRatioSeriesByMetric) and
    // computeFairValueOverlay has no yield mode, so it can't be swayed by the
    // lower chart's toggle (see this function's docstring).
    shares, displayScale, metric, xMin, xMax, price,
    cap: ratioCeiling(metric, valueBasis, 'ratio'),
  });
  // Fixed blue for the avg valuation line + ±1σ band (see
  // MAIN_AVG_VALUATION_COLOR) — NOT the per-metric METRIC_COLORS, so the line
  // reads as one valuation reference and stays blue for every metric.
  const color = MAIN_AVG_VALUATION_COLOR;
  const ratioLabel = ratioName(metric, valueBasis);

  // The avg fair-value line's points, each carrying its own ±1σ low/high (see
  // withBandBounds) so the avg line's tooltip range can show it AND the Price
  // row's valuation arrow can read the band edges live off these points
  // (nearest the hovered date). Recomputed per window on drag-zoom (see
  // recomputeOverlaysForWindow), which keeps both correct across zoom.
  const avgBounds = withBandBounds(avgData, bandData);

  // Per-date valuation ratio (value ÷ metric on the chosen anchor) keyed by
  // timestamp, from the same always-multiple ratio series the fair-value
  // overlay averages. Built on Price's dense daily timestamps, so the value
  // line shares these x's exactly; attached onto each point below so the HOVER
  // TOOLTIP can show the P/E at that date (value row) and its reciprocal, the
  // yield, at that date (metric row) — both tracking the Market Cap/EV toggle
  // via ratioName and scale-invariant across per-share/total. (The legend
  // shows plain names only; the single latest blended figure lives in the Key
  // Facts panel — see Analysis.js. These are the per-hover ones.)
  const ratioSeries = (fairValueRatioSeriesByMetric[metric] || []).filter(([, r]) => r != null);
  // Exact per-timestamp ratio for the Price line, whose points sit on price's
  // own daily grid (so an exact hit is the norm there); the nearest-lookup
  // ratioAt below is for the sparse, non-price-aligned lines.
  const ratioByTs = new Map(ratioSeries.map(([t, r]) => [t, r]));

  // Nearest-by-x ratio lookup for series whose points DON'T sit on Price's
  // daily grid — specifically the fair-value line, drawn on the metric's own
  // sparse report timestamps (not price-aligned), so an exact hit is rare. A
  // report date falls between two price days; the ratio barely moves across
  // one day, so the nearest price-day ratio is the right figure. Null past the
  // ratio series' coverage (the forward-estimate tail). Shared with the avg
  // line's enrichment below (see ratioAtFrom).
  const ratioAt = (t) => ratioAtFrom(ratioSeries, t);

  // Analyst high/low band + contributor count per forward-estimate date, keyed
  // by the metric's own estimate timestamps (the backend keys estimatesDetail
  // by exactly these), attached onto the matching avg points below so the
  // avg-line tooltip renderer can show them on estimate points. See
  // enrichAvgPoints.
  const estDetailByTs = estDetailMap(estimatesDetail[metric], metric, displayScale, shares);

  // Enrich each avg point with the per-date valuation `ratio` (P/E, so the
  // tooltip can show the earnings YIELD as its reciprocal) and, on the
  // forward-estimate points only, the analyst high/low band + contributor
  // count (`estDetail`). All these rows render off the avg line's renderer
  // (fairValueTooltipRow includeRaw:false), which sits right after the Price
  // row in the shared box, so "Price @ selected/avg multiple", "Earnings", and
  // the estimate detail read as one grouped block. `estDetail` is undefined on
  // historical points and the renderer simply omits those rows.
  const avgWithBounds = enrichAvgPoints(avgBounds, ratioSeries, estDetailByTs);

  // Keep only years with a positive dividend — zeros produce no chart point,
  // no line segment, and no hover label. Handles: never-payers (ENPH → empty),
  // long-lapsed specials (VRSN 2010-2011 then nothing until 2025 → no 13-year
  // zero line), and tail-off after a one-time (MNST 1990 → single point).
  const dividendSeries = (metricSeriesByMetric.dividends || []).filter(([, v]) => v != null && v > 0);

  // Dividend YIELD per report date: dividend-per-share ÷ price-per-share at
  // that date. Step-hold the raw daily price onto the (sparse, annual)
  // dividend timestamps and divide the raw reported dividend-per-share by it.
  // Scale-invariant (per-share ÷ per-share); keyed by timestamp, attached to
  // each dividend point below. Uses price directly — dividend yield is
  // conventionally quoted against price, not EV.
  const priceAtDividend = stepJoin(dividendSeries, price);
  // Special dividends per plotted point (per share), keyed by the same
  // timestamps as the regular-only dividends series — shown on hover only.
  const specialByTs = new Map((specialDividends || []).filter(([, v]) => v != null && v > 0));
  const divYieldByTs = new Map(
    dividendSeries.map(([t, divPerShare], i) => {
      const px = priceAtDividend[i]?.[1];
      const y = px != null && px > 0 && divPerShare != null ? (divPerShare / px) * 100 : null;
      return [t, y];
    })
  );

  // Dividend overlay: shown for EVERY selected metric, scaled by the SAME
  // multiple the fair-value line uses — NOT a valuation line, just the
  // dividend stream drawn on the same visual scale as the fundamental so the
  // payout ratio (dividends vs the metric) and dividend growth read directly
  // off the chart. One point per real reported dividend (not interpolated),
  // and only across the paid span (dividendSeries is already trimmed above):
  // a never-payer (ENPH) trims to empty and gets no overlay, while a lone
  // ancient payment (MNST's 1990 distribution) plots as a single point rather
  // than a zero line running to today.
  const paysDividend = dividendSeries.length > 0;
  const dividendSeriesObjects = paysDividend ? [
    {
      id: 'dividend-overlay',
      name: METRIC_LABELS.dividends,
      // Plain 'line' (not the chart's default areaspline): draws straight
      // segments between the sparse annual dividend points, rather than a
      // spline that curves through them or an area fill under them. The
      // dividend overlay is a bare reference line, so a straight connect
      // reads most honestly ("this year's dividend to next year's").
      type: 'line',
      data: logSafe(
        // Attach the per-date dividend yield (divYieldByTs) onto each real
        // report point so the hover tooltip can show it in brackets. Kept out
        // of buildDividendFairValueSeries itself — that stays a pure
        // payout/growth overlay with no yield (see its docstring/tests).
        buildDividendFairValueSeries(dividendSeries, shares, multiple, displayScale)
          .map((p) => ({ ...p, divYield: divYieldByTs.get(p.x), specialDividend: specialByTs.get(p.x) })),
        axisScale,
      ),
      color: '#f5c518',
      // Solid/continuous line (no dashStyle). A constantly-visible circle
      // marker on every real dividend point, mirroring how the fair-value
      // line marks its report dates with triangles — so each reported
      // dividend reads as a clear data point. Every point here is a real
      // report (the series is never interpolated), so no per-point marker
      // suppression is needed.
      lineWidth: 2,
      marker: {
        enabled: true,
        // 2/3 of the original radius 4 (half — radius 2 — read too small).
        radius: 2.67,
        symbol: 'circle',
        fillColor: 'white',
        lineColor: '#f5c518',
        lineWidth: 2,
      },
      custom: { tooltipRow: dividendTooltipRow(displayScale) },
      // Break the line when consecutive points are >18 months apart (VRSN:
      // specials in 2011, then nothing until 2025 — no diagonal across the gap).
      gapSize: EIGHTEEN_MONTHS_MS,
      gapUnit: 'value',
      stickyTracking: false,
      // Same object-format {x,y,rawValue,...} points as the fair-value line —
      // keep turbo off so Highcharts parses them (see the metric line).
      turboThreshold: 0,
      // Not part of the click-to-measure returns gesture: measuring an
      // annualized return between two dividend points is meaningless. Override
      // the chart-wide plotOptions.series.point click handler (chartConfig.js)
      // with a no-op for this series only, leaving its tooltip intact.
      point: { events: { click: () => {} } },
    },
  ] : [];

  return [
    {
      // Stable id so the oneToOne chart update matches this by identity to the
      // 'value-line' placeholder in chartConfig.js (not by array position).
      // Every series here is now id'd, so the dividend overlay's conditional
      // presence no longer shifts positional matches (see the dividend
      // overlay note below and the oneToOne writeup in valuationChartConfig.js).
      id: 'value-line',
      name: SERIES_NAMES[valueBasis][displayScale],
      // Object-format {x, y, ratio} points: `ratio` is the per-date value/
      // metric multiple (P/E or EV/E) from ratioByTs, carried so the hover
      // tooltip can show it in brackets (e.g. "Price (P/E 24.3)").
      // logSafe: EV can be negative (net cash > market cap).
      data: logSafe(
        buildActualSeries(price, shares, netDebt, valueBasis, displayScale)
          .map(([t, y]) => ({ x: t, y, ratio: ratioByTs.get(t) ?? null })),
        axisScale,
      ),
      // Object points need turbo off (same reason as the fair-value line).
      turboThreshold: 0,
      // Draw the stock price line ABOVE every other main-chart series —
      // including the valuation avg line (fairvalue-avg-metric, zIndex 102)
      // and its ±1σ band (101). The price is the primary reference on this
      // chart, so it must never be obscured by the overlays drawn against it.
      // Set here too (not just on the chartConfig.js placeholder) because
      // HighchartsReact's oneToOne update matches this series by id and
      // applies these options on top of the placeholder's — without it the
      // rebuilt price line would drop back below the overlays.
      zIndex: 200,
      // Row for the shared tooltip (chartConfig.js getMainChartTooltip); this
      // is the dense/daily series Highcharts anchors the hovered x to.
      custom: { tooltipRow: valueTooltipRow(displayScale, valueBasis, metric) },
    },
    {
      // Stable id matching the 'fairvalue-metric-line' placeholder in
      // chartConfig.js so the oneToOne update matches by identity.
      id: 'fairvalue-metric-line',
      name: `${METRIC_LABELS[metric]} × Multiple`,
      // Deliberately NOT price-aligned: this plot draws the fair-value line
      // (metric × multiple) from the metric's *own* reported points —
      // historical report dates plus the forward TTM/estimate tail — at
      // their native timestamps. It must never be resampled onto Price's
      // dense daily grid: doing so (a) interpolates fabricated in-between
      // metric values this plot was never meant to show, and (b) makes the
      // series density non-uniform (dense historical, sparse estimate),
      // which is what caused the estimate region to render as a row of
      // isolated markers instead of a continuous projection. Omitting the
      // `price` arg returns the raw metric series unchanged (see
      // buildFairValueSeries). The avg/±σ overlay below still price-aligns
      // for its own shared-tooltip reason — that's a smoothed statistical
      // line, not the metric series itself.
      data: logSafe(
        // Attach the per-date ratio (nearest price-day, since these points sit
        // on the metric's own timestamps) so the tooltip's metric row can show
        // the yield (its reciprocal) in brackets, e.g. "Earnings (4.12%)".
        buildFairValueSeries(metricSeriesByMetric[metric], shares, multiple, displayScale, metric)
          .map((p) => ({ ...p, ratio: ratioAt(p.x) })),
        axisScale,
      ),
      // Row for the shared tooltip: nearest actual report/estimate point to
      // the hovered date (see chartConfig.js getMainChartTooltip). This
      // series sits on sparse timestamps, so without the nearest-point
      // lookup it would drop out of the merged box entirely.
      custom: { tooltipRow: fairValueTooltipRow(metric, displayScale, valueBasis, multiple) },
      // Kept at 0 (turbo off) so Highcharts always parses this series'
      // {x,y,rawValue} object format regardless of point count — turbo mode
      // silently rejects non-array data (console error #12), which had made
      // the line vanish entirely when it was still Price-aligned/dense.
      turboThreshold: 0,
      // This series carries the forward estimate tail (past price's last
      // actual point), which is what the main chart's default window extends
      // to (computeDefaultRange). Opt it into the navigator so the navigator's
      // mini-axis spans the same range as the main chart — otherwise the
      // navigator only sees the shorter Price series and its handles/labels
      // drift out of alignment with the plot above. See chartConfig.js's
      // placeholder series for the matching default.
      showInNavigator: true,
    },
    {
      // The click-to-measure performance line. Empty here: it's drawn
      // imperatively by handlePointClick.js (addPoint/removePoint) and looked
      // up via chart.get('performance-line'), so its id MUST stay stable and
      // it must never be dropped by an update. Matching this by id (not
      // position) to the 'performance-line' placeholder in chartConfig.js
      // keeps the imperatively-drawn line intact across metric/symbol
      // switches. The empty data still clears the drawn line on a full rebuild
      // (see Analysis.js resetPoints), same as before — now by identity.
      id: 'performance-line',
      data: [],
      showInLegend: false,
    },
    {
      id: 'fairvalue-band-metric',
      type: 'arearange',
      linkedTo: 'fairvalue-avg-metric',
      data: logSafe(bandData, axisScale),
      color,
      fillOpacity: 0.1,
      lineWidth: 0,
      showInLegend: false,
      enableMouseTracking: false,
      // Draw the valuation avg line and its ±σ band ABOVE the other overlay
      // series — the metric "× Multiple" line and the dividend overlay — so
      // the valuation reference isn't obscured by them. The band sits just
      // under its own avg line (101 < 102). The black Price line (value-line,
      // zIndex 200 in chartConfig.js) is the one exception: it sits above
      // these so the primary price reference is never covered.
      zIndex: 101,
    },
    // The two ±1σ band EDGE lines: thin, solid, subtle strokes tracing the
    // low and high boundaries of the shaded band, so the range's edges read as
    // faint lines that echo the middle avg line at half its weight and without
    // the dashes. Purely decorative — enableMouseTracking:false and no legend
    // item, and linked to the avg line so they show/hide with it. Same zIndex
    // tier as the band (just under the avg line at 102). Their data is derived
    // from bandData ([x, low, high]) each render; the drag-zoom recompute path
    // (recomputeOverlaysForWindow) refreshes them alongside the band.
    {
      id: 'fairvalue-band-low-metric',
      type: 'line',
      linkedTo: 'fairvalue-avg-metric',
      data: logSafe(bandData.map(([x, low]) => [x, low]), axisScale),
      color,
      lineWidth: 1,
      showInLegend: false,
      enableMouseTracking: false,
      marker: { enabled: false },
      zIndex: 101,
    },
    {
      id: 'fairvalue-band-high-metric',
      type: 'line',
      linkedTo: 'fairvalue-avg-metric',
      data: logSafe(bandData.map(([x, , high]) => [x, high]), axisScale),
      color,
      lineWidth: 1,
      showInLegend: false,
      enableMouseTracking: false,
      marker: { enabled: false },
      zIndex: 101,
    },
    {
      id: 'fairvalue-avg-metric',
      type: 'line',
      name: `${METRIC_LABELS[metric]} × Avg ${ratioLabel}`,
      // avgWithBounds: each avg point carries its own ±1σ low/high so the
      // shared tooltip's "Hist. avg valuation" row can show the range (see
      // withBandBounds / fairValueTooltipRow includeRaw:false).
      data: logSafe(avgWithBounds, axisScale),
      color,
      dashStyle: 'Dash',
      lineWidth: 2,
      showInLegend: true,
      marker: { enabled: false },
      // Above all other series (see the band's zIndex note above).
      zIndex: 102,
      // Row for the shared tooltip, same nearest-point treatment as the
      // "× Multiple" line above; this avg line IS still price-aligned/dense,
      // but going through the shared formatter keeps its row in the merged
      // box consistently with the sparse fair-value line. includeRaw:false —
      // its raw-metric row is identical to the "× Multiple" line's, so it's
      // dropped here to avoid showing the same metric value (e.g. EPS) twice.
      custom: { tooltipRow: fairValueTooltipRow(metric, displayScale, valueBasis, avgMultiple ?? 0, false, multiple) },
      // Same turboThreshold issue as the "× Multiple" series above — this
      // line is also built via buildFairValueSeries and inherits the same
      // Price-aligned point count.
      turboThreshold: 0,
    },
    // Dividend overlay: present only for dividend payers, so it
    // appears/disappears as the symbol changes. Its position in this array no
    // longer matters — every main-chart series now carries a stable, unique id
    // (value-line, fairvalue-metric-line, performance-line, fairvalue-band-
    // metric, fairvalue-avg-metric, dividend-overlay), so HighchartsReact's
    // oneToOne update matches every series by identity, not array position.
    // Adding/removing this overlay can no longer shift a positional match and
    // bleed its styling (yellow line, circle markers) onto a neighbor — the
    // regression that once forced this series to be strictly last (which put
    // yellow circles on the Avg valuation line). Kept at the end here purely
    // as a readability convention. See the oneToOne removal semantics writeup
    // in valuationChartConfig.js.
    ...dividendSeriesObjects,
  ];
};

/**
 * Builds the ratio chart's series array: for each currently ticked metric,
 * its ratio/yield line plus an avg line + ±1σ band. Only ticked metrics get
 * series objects at all — unticking a metric removes its series (via
 * HighchartsReact's default oneToOne `chart.update`, which adds/removes
 * series to match by id), rather than just hiding it as before.
 */
/**
 * Builds the avg line's legend name, carrying the current window's averaged
 * value inline (e.g. "ø P/E: 17.71" or "ø Earnings Yield: 4.20%"). The value
 * used to render as a free-floating data label pinned to the LEFT edge of the
 * plot; it now lives in the legend name at the bottom of the chart — the same
 * row where the line is selected/deselected — so it can't collide with the
 * plotted lines or get clipped at a plot margin. Kept live across drag-zoom by
 * recomputeRatioOverlaysForWindow re-`update()`ing the series name with the new
 * window's average.
 */
const avgLegendNameWithValue = (avgLabel, avgData, isYield, bandData) => {
  const avg = avgData.length ? avgData[0][1] : null;
  if (avg == null || Number.isNaN(avg)) return `ø ${avgLabel}`;
  const suffix = isYield ? '%' : '';
  const value = `${avg.toFixed(2)}${suffix}`;
  // ±1σ half-width, mirroring the main chart's hist-avg tooltip row (see
  // fairValueTooltipRow). The band is the flat [avg - std, avg + std]
  // overlay, so (high - low) / 2 == std exactly. Omitted where there's no
  // band (empty/collapsed overlay).
  const band = bandData && bandData.length ? bandData[0] : null;
  const rangeSuffix = band && band[1] != null && band[2] != null
    ? ` (±${((band[2] - band[1]) / 2).toFixed(2)}${suffix})`
    : '';
  return `ø ${avgLabel}: ${value}${rangeSuffix}`;
};

/**
 * Maps the two-point horizontal avg overlay ([[from, avg], [to, avg]]) into
 * plain Highcharts point objects. The averaged value no longer rides on an
 * in-plot data label (it now lives in the legend name — see
 * avgLegendNameWithValue), so no per-point label flags are needed here. Shared
 * by the initial series build and the imperative window-recompute path
 * (recomputeOverlaysForWindow) so a drag-zoom's setData keeps the same shape.
 */
const avgOverlayPoints = (avgData) => avgData.map((point) => ({ x: point[0], y: point[1] }));

const buildRatioChartSeries = ({
  valueBasis, ratioMode, tickedMetrics, ratioSeriesByMetric, xMin, xMax,
}) => {
  const ratioTooltip = getRatioValueTooltip(ratioMode);
  const isYield = ratioMode === 'yield';
  return tickedMetrics.flatMap((m) => {
    const ratioSeries = ratioSeriesByMetric[m];
    // Each ticked metric's own cap — e.g. Revenue's is much lower than
    // EPS's — not one absolute cap shared across every metric.
    const { avgData, bandData } = computeRatioOverlay(ratioSeries, xMin, xMax, ratioCeiling(m, valueBasis, ratioMode));
    const color = METRIC_COLORS[m];
    // Label the horizontal avg line with the metric's ratio/yield name and
    // the averaged value (e.g. "P/E: 17.71"). Only the last point of the two-
    // point [[from, avg], [to, avg]] line carries the label, so it sits at the
    // right edge of the line rather than duplicating at both ends.
    const avgLabel = isYield ? yieldName(m, valueBasis) : ratioName(m, valueBasis);
    // Legend label for the selectable average line at the bottom of the chart,
    // carrying the current window's averaged value inline (e.g. "ø P/E:
    // 17.71"). The value moved here from a left-edge in-plot data label; it's
    // kept live across zoom/pan by recomputeRatioOverlaysForWindow, which
    // re-`update()`s this name with the new window's average.
    const avgLegendName = avgLegendNameWithValue(avgLabel, avgData, isYield, bandData);
    return [
      {
        // The ±1σ band is linked to the AVG line (not the ratio line) so it
        // shows/hides together with the average when the user toggles that
        // legend item — the band is only meaningful alongside the average it
        // brackets. A linked series inherits its parent's visibility, so it
        // stays hidden by default with the avg line and appears on select.
        id: `ratio-band-${m}`,
        type: 'arearange',
        linkedTo: `ratio-avg-${m}`,
        data: bandData,
        color,
        fillOpacity: 0.1,
        lineWidth: 0,
        showInLegend: false,
        enableMouseTracking: false,
      },
      {
        // The dashed average line is a standalone, legend-selectable series
        // that is now drawn by default (visible: true) alongside its ratio
        // line, rather than sitting hidden until toggled on. Its legend item
        // at the bottom of the chart still toggles the line and its ±1σ band
        // (linked above) off/on; the averaged value rides in the legend name
        // (avgLegendNameWithValue) and stays live across zoom/pan via
        // recomputeOverlaysForWindow. NOT linked to the ratio line, so it
        // carries its own independent legend toggle.
        id: `ratio-avg-${m}`,
        type: 'line',
        name: avgLegendName,
        data: avgOverlayPoints(avgData),
        visible: true,
        color,
        dashStyle: 'Dash',
        lineWidth: 1.5,
        showInLegend: true,
        enableMouseTracking: false,
        marker: { enabled: false },
      },
      {
        id: `ratio-line-${m}`,
        name: ratioMode === 'yield' ? yieldName(m, valueBasis) : ratioName(m, valueBasis),
        data: ratioSeries,
        color,
        lineWidth: 2,
        showInLegend: true,
        tooltip: ratioTooltip,
      },
    ];
  });
};

/**
 * The ratio chart's "estimated region" plot band, generalized across
 * however many metrics are ticked: spans from the last actual price point
 * to whichever ticked metric's series reaches furthest into the forecast
 * (buildEstimatedPlotBand already no-ops to [] when a single metric has no
 * forward coverage past price, e.g. a metric whose implied estimate didn't
 * resolve — reusing it per-metric and taking the widest result covers "at
 * least one ticked metric is still estimated here").
 *
 * Extracted so both the full rebuild (buildChartOptionsForState, on
 * fetch/mode change) and the ticked-set-only rebuild (applyTickedMetrics)
 * recompute it from the *current* ticked set. Ticking a metric whose
 * forecast reaches further than the previously-ticked ones (e.g. OCF's
 * EBITDA-implied estimate extends past EPS's own consensus) must widen the
 * band to match, not leave it capped at the old set's shorter reach.
 */
const buildRatioEstimatedBand = (price, metricSeriesByMetric, tickedMetrics) => (
  tickedMetrics
    .map((m) => buildEstimatedPlotBand(price, metricSeriesByMetric[m]))
    .reduce((widest, band) => (
      band.length && (!widest.length || band[0].to > widest[0].to) ? band : widest
    ), [])
);

/**
 * Builds both charts' full option objects from one consistent snapshot of
 * state — everything `fetchData` needs after a fresh fetch, and everything
 * a client-side-only rebuild (see `applyRatioMode`) needs after recomputing
 * `ratioSeriesByMetric` without a network request. Factored out so the two
 * call sites can't drift out of sync with each other.
 */
const buildChartOptionsForState = ({
  price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple, ratioMode,
  metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, estimatesDetail, tickedMetrics,
  // The two charts window their overlay statistics independently. Callers
  // pass a window per chart; `xMin`/`xMax` remain accepted as a single-window
  // fallback (e.g. on fetch, when both charts start on the same default range,
  // and for tests that only care about one chart).
  xMin, xMax,
  mainXMin = xMin, mainXMax = xMax, ratioXMin = xMin, ratioXMax = xMax,
}) => {
  const ratioEstimatedBand = buildRatioEstimatedBand(price, metricSeriesByMetric, tickedMetrics);

  // Generated ratio series already gap points outside each metric's
  // validity ceiling (see buildRatioSeries), so this normally resolves to
  // undefined and lets Highcharts auto-scale to the remaining visible
  // values. Keep the window-aware calculation as a defensive axis guard
  // for any externally supplied or legacy series that still exceeds those
  // ceilings. Computed from the ratio chart's own window.
  const ratioAxisMax = computeRatioAxisMax(tickedMetrics, ratioSeriesByMetric, valueBasis, ratioMode, ratioXMin, ratioXMax);

  return {
    chartOptions: {
      yAxis: getYAxis(displayScale, axisScale),
      // The grey "estimated region" plot band stays — it's a concise data
      // annotation, not a disclaimer. Its "Estimated¹" label carries a
      // superscript marker pointing at the page-bottom disclaimer footer
      // (footnote id="implied-estimates"), where the full methodology note
      // lives. The band is always grey (see seriesMath.js's
      // ESTIMATED_BAND_STYLE for why). See disclaimers.js.
      xAxis: [{ plotBands: buildEstimatedPlotBand(price, metricSeriesByMetric[metric], estimateKind[metric]) }],
      series: buildMainChartSeries({
        price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple,
        metricSeriesByMetric, fairValueRatioSeriesByMetric, estimatesDetail, specialDividends, xMin: mainXMin, xMax: mainXMax,
      }),
    },
    ratioChartOptions: {
      yAxis: [{
        title: { text: getRatioYAxisTitle(ratioMode) },
        floor: ratioMode === 'ratio' ? 0 : null,
        max: ratioAxisMax,
      }],
      xAxis: [{ plotBands: ratioEstimatedBand }],
      series: buildRatioChartSeries({
        valueBasis, ratioMode, tickedMetrics, ratioSeriesByMetric, xMin: ratioXMin, xMax: ratioXMax,
      }),
    },
  };
};

/**
 * Custom hook that encapsulates stock data fetching, caching, and status management.
 *
 * @param {Function} handlePointClick - Callback for chart point click events
 * @returns {object} Stock data state and controls
 */
export const useStockData = (handlePointClick) => {
  const [status, setStatus] = useState('idle');
  // Start EMPTY, not pre-seeded with a specific company (previously 'AAPL' /
  // 'Apple Inc.'). These are the "active" values StatsPanel shows for the name
  // field + ticker; they're only meaningful once a fetch has settled. Seeding
  // them with Apple made the panel flash "Apple Inc."/"AAPL" when arriving from
  // the Screener on a different stock, before that stock's fetch resolved. The
  // default AAPL landing (bare /) still fills these — via its fetch on
  // mount (see Analysis.js), which sets the real values — not via this seed.
  const [symbolActive, setSymbolActive] = useState('');
  const [companyNameActive, setCompanyNameActive] = useState('');
  // Per-symbol company facts straight off the backend's profile lookup, for
  // the Key Facts panel. Sits alongside symbolActive/companyNameActive
  // because it has the same lifecycle: set once per successful fetch, never
  // recomputed by the client-side chart rebuilds. Either field can be null
  // when FMP's profile omits it (rendered as a dash, never coerced to 0).
  const [profileActive, setProfileActive] = useState({ sector: null, industry: null, country: null });
  const [chartOptions, setChartOptions] = useState(
    getDefaultChartOptions(handlePointClick)
  );
  const [ratioChartOptions, setRatioChartOptions] = useState(
    getDefaultRatioChartOptions()
  );
  // Raw series consumed by the "Valuation vs. Subsequent Returns" card's own
  // data hook (useValuationRegression), which does its own regression/backtest
  // math client-side — kept separate from chartOptions/ratioChartOptions since
  // those are Highcharts-shaped, not raw [ts, value][] pairs. `shares` and the
  // full `metricSeriesByMetric` map (not just eps) are exposed since the
  // card's own metric picker can select any of the app's 8 metrics, each of
  // which needs its own series to convert to per-share basis. `netDebt` is
  // exposed too, for the card's Enterprise Value basis (EV = marketCap +
  // netDebt).
  const [valuationInputs, setValuationInputs] = useState({
    price: [], shares: [], netDebt: [], epsQuarterly: [], metricSeriesByMetric: {}, sbc: [],
  });
  // Curated income-statement line items (annual + quarterly, plus the
  // reporting `currency`) consumed by the Income-Statement Sankey card. Same
  // lifecycle as profileActive — raw backend data set once per successful
  // fetch, never touched by the client-side chart rebuilds — so it's a
  // sibling value here, not part of valuationInputs (those are the [ts,value]
  // series the valuation card re-derives). Defaults to an empty shape so the
  // card degrades to its placeholder before the backend field ships.
  const [incomeStatement, setIncomeStatement] = useState({ annual: [], quarterly: [], currency: null });
  // Raw reporting-currency statement rows + isFinancial/isReit for the Quality
  // score (screener-parity engine). Null until a payload carries them.
  const [qualityInputs, setQualityInputs] = useState(null);
  // Reported-year ratio series (roa, roic, assetTurnover) when the symbol
  // payload carries them. Absent today — the Fundamentals rows stay disabled
  // and the quality score omits those inputs rather than filling 0.
  const [ratios, setRatios] = useState(null);
  // The `{ min, max }` default window fetchData applies to the main and ratio
  // charts, exposed so the Scores card (lazy-mounted, own chart ref) can open
  // on exactly the same window. `null` until the first successful fetch.
  const [defaultRange, setDefaultRange] = useState(null);
  const cache = useRef({});
  const chartRef = useRef(null);
  const ratioChartRef = useRef(null);
  // Raw fetched data + current control state, kept so tickedMetrics changes
  // and window-recompute can rebuild series client-side without a refetch.
  const lastFetchRef = useRef(null);
  // The main and ratio charts now track their visible windows entirely
  // independently: zooming/panning one no longer moves the other (see
  // hooks/useAxisSync.js). Each chart's avg/±1σ overlays and y-axis auto-cap
  // are derived from *its own* window, so we keep a separate cached window
  // per chart. Full rebuilds (mode/basis/scale/ticked toggles) re-window each
  // chart's series from its own ref below.
  const mainExtremesRef = useRef({ min: null, max: null });
  const ratioExtremesRef = useRef({ min: null, max: null });

  /**
   * Fetches stock data for the given symbol, using cache when available.
   * Preserves previous valid data on fetch failure.
   *
   * @param {object} params
   * @param {string} params.symbol - Stock ticker symbol
   * @param {number} params.multiple - Multiple to apply to the selected metric
   * @param {string} params.companyName - Company display name
   * @param {'eps'|'revenue'|'ebitda'|'ebit'|'netIncome'|'fcf'|'ocf'|'dividends'} [params.metric] - Comparison metric
   * @param {'marketCap'|'enterpriseValue'} [params.valueBasis] - Company value basis
   * @param {'perShare'|'total'} [params.displayScale] - Display scale
   * @param {'ratio'|'yield'} [params.ratioMode] - Ratio chart mode
   * @param {boolean} [params.excludeLossPeriods] - In yield mode, opt into the
   *   same non-positive/sign-crossing exclusion ratio mode always applies.
   *   No effect in ratio mode, which already excludes those periods
   *   unconditionally.
   * @param {string[]} [params.tickedMetrics] - Metrics shown, with avg/±1σ overlays, on both charts
   * @param {() => object} [params.getControls] - Returns the page's CURRENT
   *   control state (same keys as the params above, minus symbol/companyName).
   *   When given, it is read once the data has arrived and overrides the
   *   call-time control values — see the comment at its use below.
   */
  const fetchData = useCallback(async ({
    symbol, multiple, companyName, metric = 'eps',
    valueBasis = 'marketCap', displayScale = 'perShare', axisScale = 'linear', ratioMode = 'ratio',
    excludeLossPeriods = false, tickedMetrics = ['eps'], adjustForSbc = false, getControls,
  }) => {
    setStatus('fetching');

    let result;

    if (cache.current[symbol]) {
      result = cache.current[symbol];
      setStatus('fetched');
    } else {
      try {
        result = await fetchStockData(symbol);
        cache.current[symbol] = result;
        setStatus('fetched');
      } catch (error) {
        setStatus('error');
        return;
      }
    }

    // The control values passed in were captured when the fetch STARTED. Any
    // toggle the user flipped while it was in flight was applied (client-side,
    // no refetch) to the previous stock's data — or to nothing, on the first
    // load — and building from the call-time values here would then silently
    // undo it: the toggle would show one state, the chart another. So build
    // from the controls as they are NOW, the moment the data is in hand.
    if (getControls) {
      ({
        multiple, metric, valueBasis, displayScale, axisScale, ratioMode,
        excludeLossPeriods, tickedMetrics, adjustForSbc,
      } = getControls());
    }

    const price = result['price'] || [];
    const shares = result['shares'] || [];
    const netDebt = result['netDebt'] || [];
    const epsQuarterly = result['epsQuarterly'] || [];
    const estimateKind = result['estimateKind'] || {};
    // Special-dividend amount per plotted dividends point ([ts, amount],
    // nonzero entries only). The dividends series itself is regular-only;
    // this feeds the dividend overlay's hover line. Absent on older payloads.
    const specialDividends = result['specialDividends'] || [];
    const estimatesDetail = result['estimatesDetail'] || {};
    const mult = Number(multiple);

    const rawMetricSeriesByMetric = {};
    RATIO_METRICS.forEach((m) => { rawMetricSeriesByMetric[m] = result['metrics']?.[m] || []; });
    // No forward/estimate points of its own (see dataService.js) — the
    // toggle projects one itself, inside applySbcAdjustment.
    const sbcSeries = result['metrics']?.sbc || [];
    const metricSeriesByMetric = withSbcAdjustment(rawMetricSeriesByMetric, sbcSeries, adjustForSbc);

    const ratioSeriesByMetric = {};
    RATIO_METRICS.forEach((m) => {
      const series = metricSeriesByMetric[m];
      ratioSeriesByMetric[m] = buildRatioSeries(price, shares, netDebt, valueBasis, m, series, ratioMode, excludeLossPeriods);
    });
    // The upper chart's own always-multiple series, independent of ratioMode.
    const fairValueRatioSeriesByMetric = buildFairValueRatioSeriesByMetric(
      price, shares, netDebt, valueBasis, metricSeriesByMetric,
    );

    // New symbol: any previously drag-zoomed/range-selected window no
    // longer means anything, so clear it on both charts and reset the
    // cached extremes before computing overlays -- but "clear" must mean
    // "the adaptive default window" (last 15 years of this symbol's own
    // price history, or all of it if it has less), not "(null, null) ==
    // show everything unbounded". The latter is what this used to do, which
    // silently defeated rangeSelector's intended 15y default on every fetch
    // (chart.update() below never touches xAxis min/max, so whatever
    // setExtremes leaves in place here is what actually renders) -- it
    // wasn't just "not adaptive," it was actively resetting to "show all."
    // Both charts get the same window so they stay in visual/overlay sync
    // (see hooks/useAxisSync.js).
    // The default view's right edge must reach as far as whichever metric
    // series are actually relevant right now (the main chart's selected
    // `metric` plus the ratio chart's ticked metrics) extend into their
    // forecast/estimate tail — see computeDefaultRange's docstring.
    const relevantEstimateSeries = [metric, ...tickedMetrics].map((m) => metricSeriesByMetric[m]);
    const { min: defaultMin, max: defaultMax } = computeDefaultRange(price, relevantEstimateSeries);
    chartRef.current?.chart?.xAxis[0]?.setExtremes(defaultMin, defaultMax, false);
    ratioChartRef.current?.chart?.xAxis[0]?.setExtremes(defaultMin, defaultMax, false);
    // A fresh fetch resets BOTH charts to the same adaptive default window.
    // From here on they diverge independently as the user zooms/pans either
    // one (each tracked in its own ref).
    mainExtremesRef.current = { min: defaultMin, max: defaultMax };
    ratioExtremesRef.current = { min: defaultMin, max: defaultMax };

    const fairValueSkeleton = enrichSkeleton(
      buildFairValueSkeleton({ metricSeries: metricSeriesByMetric[metric], shares, displayScale, metric, price }),
      fairValueRatioSeriesByMetric[metric], estimatesDetail[metric], metric, displayScale, shares,
    );

    lastFetchRef.current = {
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple: mult, ratioMode, excludeLossPeriods,
      metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, estimatesDetail,
      fairValueSkeleton,
      // "Adjust for SBC" toggle (SCORE_CARDS.md §1.6) — kept so
      // applyAdjustForSbc can re-derive metricSeriesByMetric without a
      // refetch, on or off, without losing the un-adjusted basis.
      rawMetricSeriesByMetric, sbcSeries, adjustForSbc,
    };

    const built = buildChartOptionsForState({
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple: mult, ratioMode,
      metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, estimatesDetail, tickedMetrics,
      xMin: defaultMin, xMax: defaultMax,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);

    // `netDebt` is threaded through so the valuation card can build EV
    // multiples (EV = marketCap + netDebt) under the Enterprise Value basis —
    // it reaches the client already FX-converted onto the price axis, and was
    // previously dropped here.
    // Always the REPORTED (un-adjusted) metric series plus `sbc`: the "Adjust
    // for SBC" switch only redraws the main and ratio charts. The Scores card
    // applies FCF-after-SBC itself, so feeding it adjusted FCF would subtract
    // SBC twice.
    setValuationInputs({
      price, shares, netDebt, epsQuarterly, metricSeriesByMetric: rawMetricSeriesByMetric, sbc: sbcSeries,
    });
    setDefaultRange({ min: defaultMin, max: defaultMax });

    setSymbolActive(symbol);
    setCompanyNameActive(companyName);
    setProfileActive({ sector: result['sector'] ?? null, industry: result['industry'] ?? null, country: result['country'] ?? null });
    // Reporting-currency income-statement line items for the Sankey card.
    // Fall back to the empty shape when the backend hasn't shipped the field
    // yet (the card then shows its "unavailable" placeholder rather than
    // crashing on undefined).
    setIncomeStatement(result['incomeStatement'] || { annual: [], quarterly: [], currency: null });
    setQualityInputs(result['qualityInputs'] || null);
    setRatios(result['ratios'] ?? null);
  }, []);

  /**
   * Client-side rebuild of the ratio chart's series for a new ticked-metric
   * set, reusing the already-fetched data — no network request. Relies on
   * HighchartsReact's default oneToOne `chart.update` to add/remove series
   * by id, so unticking a metric removes its series entirely rather than
   * just hiding it. The main chart is unaffected — it only ever compares
   * price/value to the single `metric` selected via the manual-multiple
   * dropdown, not to whatever's ticked here.
   */
  const applyTickedMetrics = useCallback((tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    // Ticked metrics only affect the ratio chart, so window its rebuild to
    // the ratio chart's own current window.
    const { min, max } = ratioExtremesRef.current;
    const { price, valueBasis, ratioMode, ratioSeriesByMetric, metricSeriesByMetric } = last;

    setRatioChartOptions({
      // The widest ceiling among ticked metrics changes whenever the ticked
      // set itself changes (e.g. unticking the one metric that needed a
      // cap) -- must be recomputed here too, not just on fetch/mode change,
      // for the same reason computeRatioAxisMax's docstring gives.
      yAxis: [{ max: computeRatioAxisMax(tickedMetrics, ratioSeriesByMetric, valueBasis, ratioMode, min, max) }],
      // The estimated-region band's right edge is likewise a function of the
      // ticked set (it spans to whichever ticked metric's forecast reaches
      // furthest), so it too must be recomputed here — otherwise ticking a
      // longer-forecast metric (e.g. OCF, whose EBITDA-implied estimate
      // extends past EPS's consensus) leaves the band stuck at the previous
      // set's shorter reach and the new line runs past the grey region.
      xAxis: [{ plotBands: buildRatioEstimatedBand(price, metricSeriesByMetric, tickedMetrics) }],
      series: buildRatioChartSeries({ ...last, tickedMetrics, xMin: min, xMax: max }),
    });
  }, []);

  /**
   * Client-side rebuild of both charts for a new ratio/yield mode, reusing
   * the already-fetched raw actuals — no network request. Unlike
   * tickedMetrics (a pure display filter over already-computed ratio
   * series), switching ratioMode changes the *ratio series' own values*
   * (buildRatioSeries takes ratioMode as an input), so ratioSeriesByMetric
   * must be recomputed here, not just re-read from the last fetch — this is
   * the one client-side control that can't just reuse cached derived data.
   * Preserves the current zoom window (unlike a fresh fetchData, which
   * resets it) since the symbol/timeframe hasn't changed, only the lens.
   *
   * `excludeLossPeriods` (defaults to the last-applied value when omitted)
   * shares this same recompute path since it's also a `buildRatioSeries`
   * input, not just a display filter — see `applyExcludeLossPeriods` below,
   * which calls this internal recompute via the same logic for its own
   * dedicated control.
   */
  const applyRatioMode = useCallback((ratioMode, tickedMetrics, excludeLossPeriods) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const {
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple, metricSeriesByMetric,
      fairValueRatioSeriesByMetric, estimateKind, specialDividends,
    } = last;
    const resolvedExcludeLossPeriods = excludeLossPeriods ?? last.excludeLossPeriods;

    const ratioSeriesByMetric = {};
    RATIO_METRICS.forEach((m) => {
      ratioSeriesByMetric[m] = buildRatioSeries(
        price, shares, netDebt, valueBasis, m, metricSeriesByMetric[m], ratioMode, resolvedExcludeLossPeriods,
      );
    });

    // fairValueRatioSeriesByMetric is intentionally NOT recomputed here: the
    // upper chart's fair-value overlay is always on the multiple basis and
    // must not follow the Ratio/Yield toggle (see buildMainChartSeries).
    lastFetchRef.current = { ...last, ratioMode, excludeLossPeriods: resolvedExcludeLossPeriods, ratioSeriesByMetric };

    const main = mainExtremesRef.current;
    const ratio = ratioExtremesRef.current;
    const built = buildChartOptionsForState({
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple, ratioMode,
      metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, tickedMetrics,
      mainXMin: main.min, mainXMax: main.max, ratioXMin: ratio.min, ratioXMax: ratio.max,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);
  }, []);

  /**
   * Client-side rebuild for the yield-mode "exclude non-positive/loss
   * periods" checkbox — reuses applyRatioMode's own recompute logic (it's
   * also just a buildRatioSeries input) but keeps the current ratioMode
   * unchanged, since this control is independent of the Ratio/Yield toggle.
   * No-op in ratio mode: buildRatioSeries already ignores this flag there
   * (ratio mode's own exclusion is unconditional), so there's nothing to
   * recompute, and the checkbox itself is only ever shown in yield mode.
   */
  const applyExcludeLossPeriods = useCallback((excludeLossPeriods, tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    applyRatioMode(last.ratioMode, tickedMetrics, excludeLossPeriods);
  }, [applyRatioMode]);

  /**
   * Client-side switch of the company value basis (Market Cap / Enterprise
   * Value) for both charts — no refetch, since EV is just marketCap + netDebt
   * derived from already-fetched raw series (see buildActualSeries /
   * buildRatioSeries). Both `valueBasis` and the recomputed `multiple` (the
   * Analysis-side handler resets it to a sensible default for the new basis)
   * flow through the shared rebuild path. valueBasis is a buildRatioSeries
   * input, so ratioSeriesByMetric must be recomputed here, not just re-read —
   * same as applyRatioMode. `tickedMetrics` is passed in (it lives in Analysis
   * state, not lastFetchRef), matching applyRatioMode's contract. The current
   * zoom window is preserved.
   */
  const applyValueBasis = useCallback((valueBasis, multiple, tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const {
      price, shares, netDebt, displayScale, axisScale, metric, ratioMode, excludeLossPeriods,
      metricSeriesByMetric, estimateKind, specialDividends,
    } = last;
    const mult = Number(multiple);

    const ratioSeriesByMetric = {};
    RATIO_METRICS.forEach((m) => {
      ratioSeriesByMetric[m] = buildRatioSeries(
        price, shares, netDebt, valueBasis, m, metricSeriesByMetric[m], ratioMode, excludeLossPeriods,
      );
    });
    // valueBasis IS an upper-chart input (P/E vs EV/E), so its always-multiple
    // series must be rebuilt here too — unlike ratioMode.
    const fairValueRatioSeriesByMetric = buildFairValueRatioSeriesByMetric(
      price, shares, netDebt, valueBasis, metricSeriesByMetric,
    );

    const fairValueSkeleton = enrichSkeleton(
      last.fairValueSkeleton.map((p) => ({ x: p.x, rawValue: p.rawValue, ...(p.marker ? { marker: p.marker } : {}) })),
      fairValueRatioSeriesByMetric[metric], last.estimatesDetail?.[metric], metric, displayScale, shares,
    );
    lastFetchRef.current = { ...last, valueBasis, multiple: mult, ratioSeriesByMetric, fairValueRatioSeriesByMetric, fairValueSkeleton };

    const main = mainExtremesRef.current;
    const ratio = ratioExtremesRef.current;
    const built = buildChartOptionsForState({
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple: mult, ratioMode,
      metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, tickedMetrics,
      mainXMin: main.min, mainXMax: main.max, ratioXMin: ratio.min, ratioXMax: ratio.max,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);
  }, []);

  /**
   * Client-side toggle of "Adjust for SBC" (2026-10-30 — metrics.sbc,
   * SCORE_CARDS.md §1.6) — no refetch, same pattern as applyValueBasis:
   * `rawMetricSeriesByMetric` (the un-adjusted basis, kept in
   * `lastFetchRef` since the fetch) is the single source of truth either
   * way, so toggling on then off is lossless. Unlike applyValueBasis, the
   * actual fcf/ocf VALUES change here (not just how they're combined with
   * price), so the fair-value skeleton is rebuilt from scratch via
   * `buildFairValueSkeleton` (like applyMetric does) rather than remapped
   * from the previous one.
   */
  const applyAdjustForSbc = useCallback((adjustForSbc, tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const {
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, ratioMode, excludeLossPeriods,
      rawMetricSeriesByMetric, sbcSeries, estimateKind, specialDividends, estimatesDetail,
    } = last;

    const metricSeriesByMetric = withSbcAdjustment(rawMetricSeriesByMetric, sbcSeries, adjustForSbc);

    const ratioSeriesByMetric = {};
    RATIO_METRICS.forEach((m) => {
      ratioSeriesByMetric[m] = buildRatioSeries(
        price, shares, netDebt, valueBasis, m, metricSeriesByMetric[m], ratioMode, excludeLossPeriods,
      );
    });
    const fairValueRatioSeriesByMetric = buildFairValueRatioSeriesByMetric(
      price, shares, netDebt, valueBasis, metricSeriesByMetric,
    );
    const fairValueSkeleton = enrichSkeleton(
      buildFairValueSkeleton({ metricSeries: metricSeriesByMetric[metric], shares, displayScale, metric, price }),
      fairValueRatioSeriesByMetric[metric], estimatesDetail?.[metric], metric, displayScale, shares,
    );

    lastFetchRef.current = {
      ...last, adjustForSbc, metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, fairValueSkeleton,
    };

    const main = mainExtremesRef.current;
    const ratio = ratioExtremesRef.current;
    const built = buildChartOptionsForState({
      price, shares, netDebt, valueBasis, displayScale, axisScale, metric, multiple: last.multiple, ratioMode,
      metricSeriesByMetric, ratioSeriesByMetric, fairValueRatioSeriesByMetric, estimateKind, specialDividends, estimatesDetail, tickedMetrics,
      mainXMin: main.min, mainXMax: main.max, ratioXMin: ratio.min, ratioXMax: ratio.max,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);
  }, []);

  /**
   * Client-side switch of the main chart's comparison metric — no refetch,
   * since every metric's series is already in `metricSeriesByMetric` from the
   * fetch (keyed on symbol only). Only the main chart's fair-value line
   * depends on the single selected `metric`, so this rebuilds through the
   * shared path with the new metric and its default-reset `multiple` threaded
   * in (setMetric/setMultiple's async state updates aren't visible this tick,
   * same as applyValueBasis). The fairValueSkeleton is metric-specific, so it
   * is rebuilt here too, exactly as fetchData/applyDisplayScale do. The ratio
   * chart's ticked set is handled separately by applyTickedMetrics (the
   * Analysis handler calls both), and `tickedMetrics` is passed in to keep
   * that chart's series consistent in the rebuild. The current zoom window is
   * preserved — only the lens changed, not the symbol/timeframe.
   */
  const applyMetric = useCallback((metric, multiple, tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const mult = Number(multiple);
    const fairValueSkeleton = enrichSkeleton(
      buildFairValueSkeleton({ metricSeries: last.metricSeriesByMetric[metric], shares: last.shares, displayScale: last.displayScale, metric, price: last.price }),
      last.fairValueRatioSeriesByMetric?.[metric], last.estimatesDetail?.[metric], metric, last.displayScale, last.shares,
    );
    lastFetchRef.current = { ...last, metric, multiple: mult, fairValueSkeleton };
    const main = mainExtremesRef.current;
    const ratio = ratioExtremesRef.current;
    const built = buildChartOptionsForState({
      ...last, metric, multiple: mult, fairValueSkeleton, tickedMetrics,
      mainXMin: main.min, mainXMax: main.max, ratioXMin: ratio.min, ratioXMax: ratio.max,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);
  }, []);

  /**
   * Client-side change of the fair-value multiple applied to the selected
   * metric — no refetch (the multiple is a scalar the main chart's fair-value
   * line is scaled by, not fetched data). Only the main chart depends on it,
   * so this rebuilds via the main-chart-only path like applyAxisScale rather
   * than through the full both-charts rebuild. The current zoom window is
   * preserved.
   */
  const applyMultiple = useCallback((multiple) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const mult = Number(multiple);
    lastFetchRef.current = { ...last, multiple: mult };
    const { min, max } = mainExtremesRef.current;
    setChartOptions((prev) => ({
      ...prev,
      series: buildMainChartSeries({ ...last, multiple: mult, xMin: min, xMax: max }),
    }));
  }, []);

  /**
   * Client-side switch of the display scale (Per Share / Total) for both
   * charts — no refetch, since it's just a units re-derivation of the
   * already-fetched actuals via shares (see buildActualSeries /
   * buildFairValueSeries). Unlike valueBasis, displayScale is NOT a
   * buildRatioSeries input — the ratio's numeric value is scale-invariant
   * (see buildRatioSeries), so the cached ratioSeriesByMetric is reused
   * as-is; only the main chart's actual/fair-value lines and the tooltip
   * label units change. Rebuilds through the shared path anyway (rather than
   * a main-chart-only shortcut like applyAxisScale) so the ratio chart's
   * estimate band / axis stay derived from one consistent snapshot. The
   * current zoom window is preserved.
   */
  const applyDisplayScale = useCallback((displayScale, tickedMetrics) => {
    const last = lastFetchRef.current;
    if (!last) return;
    const fairValueSkeleton = enrichSkeleton(
      buildFairValueSkeleton({ metricSeries: last.metricSeriesByMetric[last.metric], shares: last.shares, displayScale, metric: last.metric, price: last.price }),
      last.fairValueRatioSeriesByMetric?.[last.metric], last.estimatesDetail?.[last.metric], last.metric, displayScale, last.shares,
    );
    lastFetchRef.current = { ...last, displayScale, fairValueSkeleton };
    const main = mainExtremesRef.current;
    const ratio = ratioExtremesRef.current;
    const built = buildChartOptionsForState({
      ...last, displayScale, tickedMetrics,
      mainXMin: main.min, mainXMax: main.max, ratioXMin: ratio.min, ratioXMax: ratio.max,
    });
    setChartOptions(built.chartOptions);
    setRatioChartOptions(built.ratioChartOptions);
  }, []);

  /**
   * Client-side switch of the main chart's y-axis between linear and log — no
   * refetch (only the main chart has the toggle; see ADR 0002). Rebuilds the
   * series, not just the axis: log mode re-runs logSafe over the data, and
   * updating the axis alone would leave raw nonpositive points on a log axis.
   * The current zoom window is preserved.
   */
  const applyAxisScale = useCallback((axisScale) => {
    const last = lastFetchRef.current;
    if (!last) return;
    lastFetchRef.current = { ...last, axisScale };
    // Main-chart-only toggle, so it windows to the main chart's own window.
    const { min, max } = mainExtremesRef.current;
    setChartOptions((prev) => ({
      ...prev,
      yAxis: getYAxis(last.displayScale, axisScale),
      series: buildMainChartSeries({ ...last, axisScale, xMin: min, xMax: max }),
    }));
  }, []);

  /**
   * Fast path for a live drag-zoom/pan/range-selector change on either
   * chart: recomputes just the avg/±1σ overlay series and the main avg
   * line's window-derived tooltip label (not the underlying actual/ratio
   * lines) — one overlay per ticked
   * metric on the ratio chart, and exactly one overlay (for the single
   * selected `metric`) on the main chart — and pushes the new data directly
   * onto the existing Highcharts series, bypassing React/chart.update() so
   * it stays smooth mid-drag, the same imperative pattern already used for
   * the click-to-measure performance-line in handlers/handlePointClick.js.
   * Series that don't exist yet (e.g. this fires before a pending
   * applyTickedMetrics/fetchData update has committed) are silently skipped
   * via `?.`.
   */
  const recomputeMainOverlaysForWindow = useCallback((min, max) => {
    mainExtremesRef.current = { min, max };
    const last = lastFetchRef.current;
    if (!last) return;
    const {
      axisScale, metric, valueBasis, multiple,
      fairValueRatioSeriesByMetric, fairValueSkeleton, displayScale,
    } = last;

    const mainChart = chartRef.current?.chart;

    // Stats from the ratio series over the visible window — the only
    // per-tick computation that actually depends on min/max.
    const { avg, std } = computeWindowStats(
      fairValueRatioSeriesByMetric[metric], min, max, true,
      ratioCeiling(metric, valueBasis, 'ratio'),
    );
    const lowMultiple = avg == null ? null : avg - std;
    const highMultiple = avg == null ? null : avg + std;

    // Scale the precomputed skeleton by the new multiples — O(n) scalar
    // multiply, no interpJoin/Set/basis-conversion.
    const overlay = overlayFromSkeleton(fairValueSkeleton, avg, lowMultiple, highMultiple);

    const avgSeries = mainChart?.get('fairvalue-avg-metric');
    avgSeries?.setData(logSafe(overlay.avgData, axisScale), false);
    mainChart?.get('fairvalue-band-metric')?.setData(logSafe(overlay.bandData, axisScale), false);
    // Keep the two subtle ±1σ edge lines in sync with the band on drag-zoom —
    // low/high slices of the same recomputed bandData ([x, low, high]).
    mainChart?.get('fairvalue-band-low-metric')?.setData(
      logSafe(overlay.bandData.map(([x, low]) => [x, low]), axisScale), false,
    );
    mainChart?.get('fairvalue-band-high-metric')?.setData(
      logSafe(overlay.bandData.map(([x, , high]) => [x, high]), axisScale), false,
    );

    if (typeof avgSeries?.update === 'function') {
      avgSeries.update({
        custom: {
          tooltipRow: fairValueTooltipRow(metric, displayScale, valueBasis, avg ?? 0, false, multiple),
        },
      }, false);
    }

    mainChart?.redraw();
  }, []);

  /**
   * Fast path for a live drag-zoom/pan/range-selector change on the RATIO
   * chart only: recomputes just the ratio chart's own avg/±1σ overlays (one
   * per ticked metric) and its y-axis auto-cap for the ratio chart's own
   * window, and redraws only the ratio chart. Independent of the main
   * chart's window (see recomputeMainOverlaysForWindow) — zooming the ratio
   * chart never touches the main chart's overlays or axis.
   */
  const recomputeRatioOverlaysForWindow = useCallback((tickedMetrics, min, max) => {
    ratioExtremesRef.current = { min, max };
    const last = lastFetchRef.current;
    if (!last) return;
    const { valueBasis, ratioMode, ratioSeriesByMetric } = last;
    const isYield = ratioMode === 'yield';

    const ratioChart = ratioChartRef.current?.chart;

    tickedMetrics.forEach((m) => {
      const ratioOverlay = computeRatioOverlay(ratioSeriesByMetric[m], min, max, ratioCeiling(m, valueBasis, ratioMode));
      const avgSeries = ratioChart?.get(`ratio-avg-${m}`);
      avgSeries?.setData(avgOverlayPoints(ratioOverlay.avgData), false);
      // Keep the averaged value in the legend name live for this window (it
      // moved out of the in-plot data label — see avgLegendNameWithValue).
      // `false` batches this into the single redraw() below.
      const avgLabel = isYield ? yieldName(m, valueBasis) : ratioName(m, valueBasis);
      avgSeries?.update(
        { name: avgLegendNameWithValue(avgLabel, ratioOverlay.avgData, isYield, ratioOverlay.bandData) },
        false,
      );
      ratioChart?.get(`ratio-band-${m}`)?.setData(ratioOverlay.bandData, false);
    });

    // Re-derive the ratio chart's y-axis `max` for *this* window --
    // bypassing chart.update() the same way the overlay series above do, via
    // a direct yAxis.setExtremes call, rather than going through React state
    // (which only rebuilds on fetch/mode/ticked-set changes, not on every
    // live zoom tick). Passing `undefined` for both min and max when no cap
    // is needed clears any previously-set live cap and reverts the axis to
    // true auto-scaling -- see computeRatioAxisMax's docstring for why an
    // explicit `max` can't just be left in place across window changes.
    // `false` for redraw batches this with the overlay setData calls above
    // into the single redraw() below.
    const ratioAxisMax = computeRatioAxisMax(tickedMetrics, ratioSeriesByMetric, valueBasis, ratioMode, min, max);
    ratioChart?.yAxis[0]?.setExtremes(undefined, ratioAxisMax, false);

    ratioChart?.redraw();
  }, []);

  /**
   * Combined recompute for both charts over the SAME window. Used on full
   * rebuilds (mount, symbol switch, mode/basis/scale/ticked change) where
   * both charts have just been reset to a common window — not on live
   * per-chart zoom/pan, which routes through the two chart-scoped functions
   * above so the windows stay independent.
   */
  const recomputeOverlaysForWindow = useCallback((tickedMetrics, min, max) => {
    recomputeMainOverlaysForWindow(min, max);
    recomputeRatioOverlaysForWindow(tickedMetrics, min, max);
  }, [recomputeMainOverlaysForWindow, recomputeRatioOverlaysForWindow]);

  return {
    status,
    symbolActive,
    companyNameActive,
    setCompanyNameActive,
    profileActive,
    fetchData,
    chartOptions,
    ratioChartOptions,
    valuationInputs,
    incomeStatement,
    qualityInputs,
    ratios,
    defaultRange,
    chartRef,
    ratioChartRef,
    applyTickedMetrics,
    applyRatioMode,
    applyExcludeLossPeriods,
    applyValueBasis,
    applyAdjustForSbc,
    applyDisplayScale,
    applyAxisScale,
    applyMetric,
    applyMultiple,
    recomputeOverlaysForWindow,
    recomputeMainOverlaysForWindow,
    recomputeRatioOverlaysForWindow,
  };
};
